<template>
  <v-bottom-sheet
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="guide-card rounded-xl ma-auto mb-5" max-width="95%">
      <v-icon
        class="sheet-icon"
        color="#444444"
        icon="mdi-close"
        @click="close"
      />
      <v-card-text class="guide-content" :class="{ paused }">
        <h1>{{ $t(`InstallGuide.title.${variant}`) }}</h1>
        <p class="guide-progress">
          {{ $t("InstallGuide.progress", { step: step + 1, total: steps.length }) }}
        </p>
        <!-- Story-style progress: the active segment fills over STEP_MS, then auto-advances -->
        <div class="guide-dots">
          <span
            v-for="(s, i) in steps"
            :key="i"
            :class="{ done: i < step }"
            @click="step = i"
          >
            <i
              v-if="i === step"
              :key="runId"
              class="fill"
              :style="{ animationDuration: `${STEP_MS}ms` }"
              @animationend="next"
            />
          </span>
        </div>

        <v-window
          v-model="step"
          class="guide-window"
          @pointerdown="paused = true"
          @pointerup="paused = false"
          @pointercancel="paused = false"
          @pointerleave="paused = false"
        >
          <v-window-item v-for="(s, i) in steps" :key="i" :value="i">
            <p class="guide-text">
              <span class="guide-num">{{ i + 1 }}</span>
              {{ $t(`InstallGuide.${variant}.${s.key}`) }}
            </p>

            <!-- Illustration: mimics the button the user should look for -->
            <div v-if="s.mock" class="guide-mock" :class="s.mockClass">
              <span
                v-for="(m, j) in s.mock"
                :key="j"
                class="mock-item"
                :class="{ hi: m.hi, text: !m.icon, toggle: m.toggle, primary: m.primary }"
              >
                <v-icon v-if="m.icon" :icon="m.icon" size="20" />
                <span v-if="m.label">{{ m.label }}</span>
                <span v-if="m.toggle" class="mock-switch" />
              </span>
            </div>
            <img v-if="s.img" class="guide-img" :src="s.img" alt="" />

            <v-btn
              v-if="s.copy"
              class="guide-copy-btn"
              color="#bd9478"
              rounded="lg"
              :prepend-icon="copied ? 'mdi-check' : 'mdi-content-copy'"
              @click="copyLink"
            >
              {{ copied ? $t("InstallGuide.copied") : $t("InstallGuide.copyLink") }}
            </v-btn>

            <p v-if="s.hint" class="guide-hint">
              {{ $t(`InstallGuide.${variant}.${s.hint}`) }}
            </p>

            <div v-if="s.arrow" class="guide-arrow">
              <span>{{ $t("InstallGuide.arrowHint") }}</span>
              <v-icon icon="mdi-arrow-down-bold" size="32" />
            </div>
          </v-window-item>
        </v-window>

        <div class="guide-nav">
          <v-btn
            icon="mdi-chevron-right"
            variant="tonal"
            size="small"
            :aria-label="$t('InstallGuide.prev')"
            @click="prev"
          />
          <p class="guide-hold-hint">{{ $t("InstallGuide.holdHint") }}</p>
          <v-btn
            icon="mdi-chevron-left"
            variant="tonal"
            size="small"
            :aria-label="$t('InstallGuide.next')"
            @click="next"
          />
        </div>
      </v-card-text>
    </v-card>
  </v-bottom-sheet>
</template>

<script>
import desktopIcon from "@/assets/images/desktop-icon.png";
import desktopInstall from "@/assets/images/desktop-pwa-install.png";

const ua = window.navigator.userAgent;
const isIPad =
  /ipad/i.test(ua) ||
  // iPadOS reports as desktop Safari
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isIOS = isIPad || /iphone|ipod/i.test(ua);
// Chrome/Firefox/Edge on iOS and in-app browsers (Instagram, Facebook)
const isOtherIOSBrowser = /CriOS|FxiOS|EdgiOS|Instagram|FBAN|FBAV/.test(ua);
// iOS 26 freezes the OS version in the UA at 18_6, but Safari's own
// "Version/26.x" token is accurate.
const safariMajor = Number(ua.match(/Version\/(\d+)/)?.[1] || 0);

function detectVariant() {
  if (isIOS && isOtherIOSBrowser) return "iosOther";
  if (isIPad) return "ipad";
  if (isIOS) return safariMajor >= 26 ? "ios26" : "iosClassic";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

const SHARE = "mdi-export-variant";
const ADD_HOME = { icon: "mdi-plus-box-outline", label: "הוספה למסך הבית", hi: true };
const ADD_BTN = [
  { label: "הוספה למסך הבית" },
  { label: "הוספה", hi: true, primary: true },
];

// How long each step is shown before auto-advancing (steps loop).
const STEP_MS = 5000;

const STEPS = {
  ios26: [
    {
      key: "s1",
      hint: "s1Hint",
      arrow: true,
      mockClass: "bar",
      mock: [
        // Listed right-to-left (RTL flex): on a Hebrew iOS 26 ☰ is on the right, ↻ on the left
        { icon: "mdi-menu", hi: true },
        { label: "rgift.co.il" },
        { icon: "mdi-refresh" },
      ],
    },
    { key: "s2", mock: [{ icon: SHARE, label: "שיתוף", hi: true }] },
    { key: "s3", mock: [{ icon: "mdi-chevron-down", label: "הצגת עוד", hi: true }] },
    { key: "s4", mock: [ADD_HOME] },
    {
      key: "s5",
      mockClass: "column",
      mock: [
        { label: "פתיחה ביישום רשת", toggle: true },
        { label: "הוספה", hi: true, primary: true },
      ],
    },
  ],
  iosClassic: [
    {
      key: "s1",
      arrow: true,
      mockClass: "bar",
      mock: [
        { icon: "mdi-chevron-right" },
        { icon: "mdi-chevron-left" },
        { icon: SHARE, hi: true },
        { icon: "mdi-book-open-outline" },
        { icon: "mdi-checkbox-multiple-blank-outline" },
      ],
    },
    { key: "s2", mock: [ADD_HOME] },
    { key: "s3", mockClass: "bar", mock: ADD_BTN },
  ],
  ipad: [
    {
      key: "s1",
      hint: "s1Hint",
      mockClass: "bar",
      mock: [
        { label: "rgift.co.il" },
        { icon: SHARE, hi: true },
        { icon: "mdi-plus" },
      ],
    },
    { key: "s2", mock: [ADD_HOME] },
    { key: "s3", mockClass: "bar", mock: ADD_BTN },
  ],
  iosOther: [
    { key: "s1", copy: true },
    { key: "s2", mock: [{ icon: "mdi-compass-outline", label: "Safari", hi: true }] },
  ],
  android: [
    { key: "s1", mockClass: "bar", mock: [{ label: "rgift.co.il" }, { icon: "mdi-dots-vertical", hi: true }] },
    { key: "s2", mock: [{ icon: "mdi-cellphone-arrow-down-variant", label: "התקנת אפליקציה", hi: true }] },
    { key: "s3", mock: [{ label: "התקנה", hi: true, primary: true }] },
  ],
  desktop: [
    { key: "s1", img: desktopIcon },
    { key: "s2", img: desktopInstall },
  ],
};

export default {
  props: {
    modelValue: Boolean,
  },
  emits: ["update:modelValue"],
  data() {
    return {
      variant: detectVariant(),
      STEP_MS,
      step: 0,
      runId: 0, // bumped on open so the active bar restarts even on step 0
      paused: false,
      copied: false,
    };
  },
  computed: {
    steps() {
      return STEPS[this.variant];
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.step = 0;
        this.runId++;
        this.paused = false;
        this.copied = false;
      }
    },
  },
  methods: {
    next() {
      if (!this.modelValue) return;
      this.step = (this.step + 1) % this.steps.length;
      if (this.step === 0) this.runId++; // single-step variants would never restart
    },
    prev() {
      const n = this.steps.length;
      this.step = (this.step - 1 + n) % n;
      if (n === 1) this.runId++;
    },
    close() {
      this.$emit("update:modelValue", false);
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(window.location.origin);
        this.copied = true;
      } catch (err) {
        console.log(err);
      }
    },
  },
};
</script>

<style lang="scss">
.v-overlay__content.v-bottom-sheet__content {
  box-shadow: unset !important;
}
</style>

<style scoped lang="scss">
.guide-card {
  position: relative;
}

.sheet-icon {
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 1;
}

.guide-content {
  text-align: right;
  padding-top: 20px;

  h1 {
    font-size: 26px;
    color: $blackColor;
    margin: 0;
    padding-left: 32px; // clear the close icon
  }
}

.guide-progress {
  color: #7c7c7c;
  font-size: 14px;
  margin: 2px 0 6px;
}

.guide-dots {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;

  span {
    flex: 1;
    height: 4px;
    border-radius: 2px;
    background: #e6e6e6;
    transition: background 0.3s;

    position: relative;
    overflow: hidden;
    cursor: pointer;

    &.done {
      background: #bd9478;
    }

    .fill {
      position: absolute;
      inset: 0;
      background: #bd9478;
      transform-origin: right; // RTL: fills from the right
      animation: guide-fill linear forwards;
    }
  }
}

.paused .guide-dots .fill {
  animation-play-state: paused;
}

@keyframes guide-fill {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

.guide-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;

  .v-btn {
    color: #bd9478;
  }
}

.guide-hold-hint {
  flex: 1;
  text-align: center;
  font-size: 12px;
  color: #9e9e9e;
  margin: 0;
}

.guide-window {
  min-height: 170px;
  // Hold-to-pause must not trigger iOS text selection / callout
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

.guide-text {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: $mediumTextSize;
  color: $blackColor;
  margin-bottom: 14px;
}

.guide-num {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #bd9478;
  color: #fff;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.guide-mock {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 14px;
  background: #f2f2f7;
  border-radius: 16px;

  &.bar {
    justify-content: space-around;
    border-radius: 28px;
  }

  &.column {
    flex-direction: column;
    align-items: stretch;
  }
}

.mock-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 12px;
  background: #fff;
  color: #1c1c1e;
  font-size: 15px;

  .bar &.text {
    background: transparent;
    color: #6e6e73;
  }

  &.toggle {
    justify-content: space-between;
  }

  // iOS-style "on" switch (knob on the left in RTL)
  .mock-switch {
    position: relative;
    width: 44px;
    height: 26px;
    border-radius: 13px;
    background: #34c759;

    &::after {
      content: "";
      position: absolute;
      top: 2px;
      left: 2px;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: #fff;
    }
  }

  &.primary {
    background: #0a84ff;
    color: #fff;
    justify-content: center;
    font-weight: 600;
  }

  &.hi {
    animation: guide-pulse 1.4s ease-in-out infinite;
  }
}

@keyframes guide-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(189, 148, 120, 0.8);
  }
  50% {
    box-shadow: 0 0 0 7px rgba(189, 148, 120, 0);
  }
}

.guide-img {
  display: block;
  max-width: 100%;
  margin: 0 auto;
  border-radius: 8px;
}

.guide-copy-btn {
  color: #fff !important;
}

.guide-hint {
  font-size: 14px;
  color: #7c7c7c;
  margin-top: 10px;
}

.guide-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
  color: #bd9478;
  font-weight: 600;
  font-size: 14px;

  .v-icon {
    animation: guide-bounce 1s ease-in-out infinite;
  }
}

@keyframes guide-bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(8px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .mock-item.hi,
  .guide-arrow .v-icon {
    animation: none;
  }
}
</style>
